import { IAlert } from "./alert";

/**
 * Email types for the Send Email functionality
 */
export const EMAIL_TYPES = [
    "morning-update",
    "market-wrap",
    "alert-to-lock",
    "saturday",
    "strike-rate",
] as const;

export type TEmailType = (typeof EMAIL_TYPES)[number];

export const EMAIL_TYPE_LABELS: Record<TEmailType, string> = {
    "morning-update": "Morning Update",
    "market-wrap": "Daily Market Wrap",
    "alert-to-lock": "Alert to Lock",
    saturday: "Saturday",
    "strike-rate": "Strike Rate",
};

export interface ICampaignDetails {
    lastSent: string;
    name: string;
    campaignId: string;
    emailType: TEmailType;
}

export interface IEmailModulesPayloadUpcomingNewsItem {
    newsItem: string;
    time?: string;
}

export interface IEmailModulesPayloadUpcomingNews {
    enabled: boolean;
    title: string;
    dates?: {
        monday?: string;
        tuesday?: string;
        wednesday?: string;
        thursday?: string;
        friday?: string;
    };
    monday: IEmailModulesPayloadUpcomingNewsItem[];
    tuesday: IEmailModulesPayloadUpcomingNewsItem[];
    wednesday: IEmailModulesPayloadUpcomingNewsItem[];
    thursday: IEmailModulesPayloadUpcomingNewsItem[];
    friday: IEmailModulesPayloadUpcomingNewsItem[];
}

export interface IEmailModulesPayloadSocialShare {
    enabled: boolean;
    title: string;
    cards: string[];
}

export interface IEmailModulesPayloadSocialStudio {
    enabled: boolean;
    title: string;
    cards: string[];
}

export interface IEmailModulesPayloadHeader {
    enabled: boolean;
    title: string;
    subtitle: string;
}

export interface IEmailModulesPayloadVideo {
    enabled: boolean;
    videoUrl: string;
    imageUrl?: string;
    url: string;
}

export interface IEmailModulesPayloadNewsletter {
    enabled: boolean;
    title: string;
    previewText: string;
    url: string;
    buttonText: string;
}

export interface IEmailModulesPayloadBlog {
    enabled: boolean;
    title: string;
    previewText: string;
    url: string;
    buttonText: string;
}

export interface IEmailModulesPayloadPromo {
    enabled: boolean;
    id: string | null;
}

export interface IEmailModulesPayloadPresentationExpressway {
    enabled: boolean;
    id: string | null;
}

export interface IEmailModulesPayloadWebinarCard {
    title: string;
    imageUrl: string;
    description?: string;
    date: string;
    time: string;
    timezone: string;
    url: string;
    buttonText?: string;
}

export interface IEmailModulesPayloadWebinar {
    enabled: boolean;
    cards: IEmailModulesPayloadWebinarCard[];
}

export interface IEmailModulesPayload {
    header: IEmailModulesPayloadHeader;
    video: IEmailModulesPayloadVideo;
    socialShare: IEmailModulesPayloadSocialShare;
    socialStudioScripts: IEmailModulesPayloadSocialStudio;
    upcomingNews: IEmailModulesPayloadUpcomingNews;
    newsletter: IEmailModulesPayloadNewsletter;
    blog: IEmailModulesPayloadBlog;
    promo: IEmailModulesPayloadPromo;
    presentationExpressway: IEmailModulesPayloadPresentationExpressway;
    webinar: IEmailModulesPayloadWebinar;
}

/**
 * Alert-derived trigger data for the automated `alert-to-lock` email (MIG-8057). Unlike the admin
 * module payload, it carries no modules — only the fields the Liquid template needs.
 */
export interface IAlertEmailData {
    type: IAlert["type"];
    body?: string;
    marketSnapshot: IAlert["marketSnapshot"];
}

export interface IAlertEmailPayload {
    alert: IAlertEmailData;
}

/**
 * Payload accepted by BrazeService.sendEmail/sendTestEmail. Module-based emails (the admin Send Email
 * page) use {@link IEmailModulesPayload}; the automated alert-to-lock email uses {@link IAlertEmailPayload}.
 */
export type TBrazeEmailPayload = IEmailModulesPayload | IAlertEmailPayload;

/** One loan preview row in the Strike Rate alert email. */
export interface IStrikeRateEmailLoan {
    buyer: string;
    loanAmount: string;
    loanRate: number;
    currentMortgageRate: number;
    estimate: boolean;
    paymentSavings: boolean;
    miSavings: boolean;
    savings: string;
}

/** Progress of a Strike Rate full send, polled by the admin page and by mbs-jobs. */
export interface IStrikeRateSendRun {
    runId: string;
    status: "running" | "completed" | "failed";
    total: number;
    processed: number;
    sent: number;
    skipped: number;
    failed: number;
    percentComplete: number;
    startedAt: string;
    finishedAt?: string;
    error?: string;
}
